<template>
  <div class="war-room-avatar-uploader">
    <div class="war-room-avatar-uploader__round" :class="{ 'war-room-avatar-uploader__round--empty': !image }">
      <div class="war-room-avatar-uploader__input-wrapper">
        <input
          ref="input"
          type="file"
          accept="image/jpeg, image/jpg, image/png, image/webp"
          class="war-room-avatar-uploader__input"
          data-test="file-input"
          @change="handleFileChange"
        />
      </div>
      <div v-if="image" class="war-room-avatar-uploader__image-wrapper">
        <img class="war-room-avatar-uploader__image" :src="image" data-test="image-block" />
      </div>
      <div v-else class="war-room-avatar-uploader__empty">
        <IconCamera />
      </div>
    </div>
    <div class="war-room-avatar-uploader__avatar-label" @click="handleClick">
      {{ label }}
    </div>
  </div>
</template>

<script>
const MAX_FILE_SIZE = 5;
const FILE_SIZE_ERROR_MESSAGE = `Maximum image size must be lower than ${MAX_FILE_SIZE}Mb`;

export default {
  name: 'WarRoomAvatarUploader',
  components: {
    IconCamera: () => import('@/components/Icon/Camera.vue'),
  },
  props: {
    imageSrc: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      imageBlob: null,
    };
  },
  computed: {
    label() {
      if (!this.image) {
        return 'Upload a profile picture';
      } else {
        return 'Remove picture';
      }
    },
    image() {
      return this.imageBlob || this.imageSrc;
    },
  },
  methods: {
    handleClick() {
      if (!this.image) {
        this.$refs.input.click();
        return;
      }

      this.$refs.input.value = '';
      this.imageBlob = null;
      this.$emit('file-change', null);
    },
    handleFileChange(event) {
      const files = event.target.files || event.dataTransfer.files;

      if (FileReader && files && files.length) {
        if (files[0].size > 1024 ** 2 * MAX_FILE_SIZE) {
          this.$notice.error(FILE_SIZE_ERROR_MESSAGE);
          return;
        }

        const fileReader = new FileReader();
        fileReader.onload = () => {
          this.imageBlob = fileReader.result;
        };

        fileReader.readAsDataURL(files[0]);
        this.$emit('file-change', files[0]);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.war-room-avatar-uploader {
  display: flex;
  flex-direction: column;
  align-items: center;

  &__round {
    width: 104px;
    height: 104px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    overflow: hidden;
    position: relative;

    @include mq('sm') {
      width: 104px;
      height: 104px;
    }
  }

  &__round--empty {
    border: 1px dashed var(--theme-color-border);
  }

  &__input-wrapper {
    position: absolute;
    left: 0;
    top: 0;
    opacity: 0;
  }

  &__input {
    width: 104px;
    height: 104px;
    cursor: pointer;
  }

  &__empty {
    position: absolute;
    left: 0;
    top: 0;
    right: 0;
    bottom: 0;
    pointer-events: none;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: var(--theme-color-card);
  }

  &__image-wrapper {
    position: absolute;
    left: 0;
    top: 0;
    right: 0;
    bottom: 0;
    pointer-events: none;
  }

  &__image {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &__avatar-label {
    @include caption2;

    color: var(--universal-color-gray-50);
    width: 73px;
    text-align: center;
    margin-top: 8px;
    cursor: pointer;
  }
}
</style>
